import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import getApiBase from "./getApiBase";
import carLeft from "../assets/review-shapes/car-left.png";
import suvLeft from "../assets/review-shapes/suv-left.png";
import truckLeft from "../assets/review-shapes/truck-left.png";

// Weighted array of vehicle shapes for random selection
// Ratio: cars 2/5, SUVs 2/5, trucks 1/5
const getRandomVehicle = () => {
  const rand = Math.random();
  if (rand < 0.4) return carLeft;      // 40% chance
  if (rand < 0.8) return suvLeft;      // 40% chance
  return truckLeft;                     // 20% chance
};

const ReviewTicker = () => {
  const [reviews, setReviews] = useState([]);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    // Try to get user's location
    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            console.log("Location access denied or unavailable:", error);
            // Continue without location - reviews will be randomized
            fetchReviews();
          }
        );
      } else {
        // Geolocation not supported - fetch without location
        fetchReviews();
      }
    };

    getUserLocation();
  }, []);

  useEffect(() => {
    // Fetch reviews when location is obtained or skip if denied
    if (userLocation !== null) {
      fetchReviews(userLocation.lat, userLocation.lng);
    }
  }, [userLocation]);

  const fetchReviews = async (lat = null, lng = null) => {
    try {
      const params = new URLSearchParams();
      if (lat && lng) {
        params.append("lat", lat);
        params.append("lng", lng);
      }

      const response = await fetch(
        `${getApiBase()}/api/reviews/public?${params.toString()}`
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch reviews");
      }

      const data = await response.json();
      
      // Duplicate reviews and assign random vehicle shapes to EACH copy for true randomness
      if (data.length > 0) {
        const tripled = [...data, ...data, ...data];
        const reviewsWithVehicles = tripled.map(review => ({
          ...review,
          vehicleShape: getRandomVehicle() // Assign random vehicle to each copy
        }));
        setReviews(reviewsWithVehicles);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        sx={{
          fontSize: "1.2rem",
          color: index < rating ? "#FFD700" : "#e0e0e0",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
        }}
      />
    ));
  };

  if (reviews.length === 0) {
    return null; // Don't show ticker if no reviews
  }

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        my: 4,
      }}
    >
      {/* Road Background Container */}
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          position: "relative",
          py: 4,
          background: "linear-gradient(to bottom, #4a4a4a 0%, #3a3a3a 50%, #4a4a4a 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: "4px",
            background: "repeating-linear-gradient(to right, #FDB515 0px, #FDB515 30px, transparent 30px, transparent 50px)",
            transform: "translateY(-50%)",
          },
        }}
      >
      <Box
        sx={{
          display: "flex",
          gap: 4,
          animation: "scroll 60s linear infinite",
          "&:hover": {
            animationPlayState: "paused",
          },
          "@keyframes scroll": {
            "0%": {
              transform: "translateX(0)",
            },
            "100%": {
              transform: "translateX(-33.333%)",
            },
          },
        }}
      >
        {reviews.map((review, index) => (
          <Box
            key={`${review.id}-${index}`}
            sx={{
              minWidth: "450px",
              height: "180px",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage: `url(${review.vehicleShape})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
            }}
          >
            {/* Inner content box - positioned in center of vehicle, shifted right 35px */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                bgcolor: "transparent",
                borderRadius: 2,
                p: 1.5,
                maxWidth: "320px",
                position: "absolute",
                top: "50%",
                left: "calc(50% + 35px)",
                transform: "translate(-50%, -50%)",
              }}
            >
            {/* Star Rating */}
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {renderStars(review.rating)}
            </Box>

            {/* Comment */}
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.85rem",
                color: "#3a3a3a",
                fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.3,
                fontWeight: 500,
              }}
            >
              "{review.comment}"
            </Typography>

            {/* Shop Name and Location */}
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  color: "#e63926",
                  fontWeight: 700,
                }}
              >
                {review.shopName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#3a3a3a",
                  fontWeight: 500,
                }}
              >
                {review.city}, {review.state}
              </Typography>
            </Box>
            </Box>
          </Box>
        ))}
      </Box>
      </Box>
    </Box>
  );
};

export default ReviewTicker;

